import axios from "axios";
import { isAuthLoginError } from "./authLoginError";

export type AuthFormErrorKind =
  | "wrong_credentials"
  | "security_check"
  | "invalid_token"
  | "throttled"
  | "network"
  | "server"
  | "waking";

export type AuthFormError = {
  kind: AuthFormErrorKind;
  messageKey: string;
};

const TRANSIENT_STATUSES = new Set([502, 503, 504]);

export function isTransientBackendStatus(status: number | undefined): boolean {
  return status !== undefined && TRANSIENT_STATUSES.has(status);
}

export function resolveLoginFormError(
  error: unknown,
  options?: { isWakingUp?: boolean },
): AuthFormError | null {
  if (isAuthLoginError(error)) {
    return resolveLoginFormError(error.cause, options);
  }

  if (axios.isCancel(error)) {
    return null;
  }

  if (!axios.isAxiosError(error)) {
    return { kind: "network", messageKey: "errors.connection" };
  }

  const status = error.response?.status;

  if (status === 401 || status === 400) {
    return { kind: "wrong_credentials", messageKey: "login.wrongCredentials" };
  }

  if (status === 403) {
    return { kind: "security_check", messageKey: "login.securityCheckFailed" };
  }

  if (status === 429) {
    return { kind: "throttled", messageKey: "auth.throttled" };
  }

  if (status === 503 && error.response?.data?.status === "warming") {
    return options?.isWakingUp ? null : { kind: "waking", messageKey: "login.serviceStarting" };
  }

  if (!error.response) {
    if (options?.isWakingUp) {
      return null;
    }
    return { kind: "network", messageKey: "errors.connection" };
  }

  if (isTransientBackendStatus(status)) {
    if (options?.isWakingUp) {
      return null;
    }
    return { kind: "waking", messageKey: "errors.connection" };
  }

  if (status !== undefined && status >= 500) {
    return { kind: "server", messageKey: "errors.server" };
  }

  return { kind: "wrong_credentials", messageKey: "login.wrongCredentials" };
}

export type PasswordResetRequestContext = "forgot" | "confirm";

export function resolvePasswordResetRequestError(
  error: unknown,
  options?: { context?: PasswordResetRequestContext },
): AuthFormError | null {
  const context = options?.context ?? "forgot";

  if (axios.isCancel(error)) {
    return null;
  }

  if (!axios.isAxiosError(error)) {
    return { kind: "network", messageKey: "errors.connection" };
  }

  if (error.response?.status === 429) {
    return { kind: "throttled", messageKey: "auth.throttled" };
  }

  if (!error.response || isTransientBackendStatus(error.response.status)) {
    return { kind: "network", messageKey: "errors.connection" };
  }

  const status = error.response.status;

  if (status >= 500) {
    return { kind: "server", messageKey: "errors.server" };
  }

  if (status === 403) {
    return { kind: "security_check", messageKey: "login.securityCheckFailed" };
  }

  if (context === "confirm" && (status === 401 || status === 404)) {
    return { kind: "invalid_token", messageKey: "passwordReset.invalidTokenMessage" };
  }

  return null;
}

export type FieldErrors = Record<string, string[]>;

export function extractResponseDetail(data: unknown): string | undefined {
  if (!data || typeof data !== "object" || !("detail" in data)) {
    return undefined;
  }
  const detail = (data as { detail?: unknown }).detail;
  if (typeof detail === "string" && detail.trim().length > 0) {
    return detail;
  }
  return undefined;
}

export function extractFieldErrors(data: unknown): FieldErrors {
  if (!data || typeof data !== "object") {
    return {};
  }

  const result: FieldErrors = {};
  for (const [key, value] of Object.entries(data as Record<string, unknown>)) {
    if (Array.isArray(value)) {
      const messages = value.filter((item): item is string => typeof item === "string");
      if (messages.length > 0) {
        result[key] = messages;
      }
    } else if (typeof value === "string") {
      result[key] = [value];
    }
  }
  return result;
}

export function firstFieldError(
  fieldErrors: FieldErrors,
  field: string,
): string | undefined {
  return fieldErrors[field]?.[0];
}

const RESET_PASSWORD_FORM_FIELDS = new Set(["new_password", "confirm_password"]);

const RESET_PASSWORD_FIELD_ALIASES: Record<string, string> = {
  password: "new_password",
};

export type MappedPasswordResetFieldErrors = {
  mapped: Record<string, string>;
  unmatchedMessages: string[];
};

/** Map API field keys to reset-password form fields; collect unmatched messages for a general alert. */
export function mapPasswordResetServerFieldErrors(
  fieldErrors: FieldErrors,
): MappedPasswordResetFieldErrors {
  const mapped: Record<string, string> = {};
  const unmatchedMessages: string[] = [];

  for (const [field, messages] of Object.entries(fieldErrors)) {
    if (
      field === "non_field_errors" ||
      field === "detail" ||
      field === "token" ||
      field === "uid"
    ) {
      continue;
    }
    const message = messages[0];
    if (!message) {
      continue;
    }

    const target = RESET_PASSWORD_FIELD_ALIASES[field] ?? field;
    if (RESET_PASSWORD_FORM_FIELDS.has(target)) {
      mapped[target] = message;
    } else {
      unmatchedMessages.push(message);
    }
  }

  return { mapped, unmatchedMessages };
}

const PASSWORD_RESET_TOKEN_ERROR_CODES = new Set([
  "invalid_token",
  "token_invalid",
  "token_expired",
  "token_used",
]);

export function isPasswordResetTokenInvalid(
  data: unknown,
  fieldErrors: FieldErrors,
): boolean {
  if (data && typeof data === "object" && "code" in data) {
    const code = (data as { code?: unknown }).code;
    if (typeof code === "string" && PASSWORD_RESET_TOKEN_ERROR_CODES.has(code)) {
      return true;
    }
  }
  return Boolean(fieldErrors.token?.length || fieldErrors.uid?.length);
}
